import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Activity, Play, RefreshCw, XCircle, Search, TrendingUp, TrendingDown,
  BarChart3, Clock, CheckCircle, AlertTriangle, TestTube, Settings,
  Calendar, Database, Zap, Timer, Bug
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AlphaPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState([]);
  const [searchSymbol, setSearchSymbol] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [config, setConfig] = useState({});
  const [configDirty, setConfigDirty] = useState(false);
  const [schedulerState, setSchedulerState] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [topAlpha, setTopAlpha] = useState({ positive: [], negative: [] });
  const [alphaPeriod, setAlphaPeriod] = useState("1d");
  const [diagnostics, setDiagnostics] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/alpha/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setStats(result);
        setRunning(result.state?.status === "running");
        if (result.config) {
          setConfig(result.config);
        }
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSchedulerState = useCallback(async () => {
    try {
      const response = await fetch(`${API}/alpha/scheduler-state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setSchedulerState(result);
      }
    } catch (error) {
      console.error("Failed to fetch scheduler state:", error);
    }
  }, [token]);

  const fetchAvailableDates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/alpha/dates?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setAvailableDates(result.dates || []);
        if (result.dates?.length > 0 && !selectedDate) {
          setSelectedDate(result.dates[0].date);
        }
      }
    } catch (error) {
      console.error("Failed to fetch dates:", error);
    }
  }, [token, selectedDate]);

  const fetchTopAlpha = useCallback(async (date, period = "1d") => {
    try {
      let posUrl, negUrl;
      
      if (period === "1d" && date) {
        // Single day - use date-based endpoint
        posUrl = `${API}/alpha/top?date=${date}&direction=positive&limit=20`;
        negUrl = `${API}/alpha/top?date=${date}&direction=negative&limit=20`;
      } else {
        // Multi-day period - use period-based endpoint
        posUrl = `${API}/alpha/top-period?period=${period}&direction=positive&limit=20`;
        negUrl = `${API}/alpha/top-period?period=${period}&direction=negative&limit=20`;
      }
      
      const [posRes, negRes] = await Promise.all([
        fetch(posUrl, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(negUrl, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (posRes.ok && negRes.ok) {
        const posData = await posRes.json();
        const negData = await negRes.json();
        setTopAlpha({
          positive: posData.data || [],
          negative: negData.data || []
        });
      }
    } catch (error) {
      console.error("Failed to fetch top alpha:", error);
    }
  }, [token]);

  const fetchData = useCallback(async (symbol = "") => {
    try {
      let url = `${API}/alpha/data?limit=100`;
      if (symbol) {
        url = `${API}/alpha/symbol/${symbol}?limit=100`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, [token]);

  const fetchDiagnostics = useCallback(async () => {
    try {
      toast.info("Loading diagnostics...");
      const response = await fetch(`${API}/alpha/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setDiagnostics(result);
        setShowDiagnostics(true);
        toast.success("Diagnostics loaded");
      } else {
        const error = await response.text();
        toast.error(`Failed to fetch diagnostics: ${response.status}`);
        console.error("Diagnostics error:", error);
      }
    } catch (error) {
      console.error("Failed to fetch diagnostics:", error);
      toast.error("Failed to fetch diagnostics: " + error.message);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    fetchSchedulerState();
    fetchAvailableDates();
    const interval = setInterval(() => {
      fetchStats();
      fetchSchedulerState();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchSchedulerState, fetchAvailableDates]);

  useEffect(() => {
    fetchTopAlpha(selectedDate, alphaPeriod);
  }, [selectedDate, alphaPeriod, fetchTopAlpha]);

  const runPipeline = async (runType) => {
    try {
      const response = await fetch(`${API}/alpha/run/${runType}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`${runType === "test" ? "Test" : runType === "full-recompute" ? "Full recompute" : "Incremental"} alpha run started`);
        setRunning(true);
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start run");
      }
    } catch (error) {
      toast.error("Failed to start run");
    }
  };

  const cancelRun = async () => {
    try {
      const response = await fetch(`${API}/alpha/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.info("Cancel signal sent");
      }
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const forceReset = async () => {
    if (!window.confirm("Force reset stuck alpha pipelines?")) return;
    try {
      const response = await fetch(`${API}/alpha/force-reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Force reset complete");
        setRunning(false);
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to force reset");
    }
  };

  const saveConfig = async () => {
    try {
      const response = await fetch(`${API}/alpha/config`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(config)
      });
      if (response.ok) {
        toast.success("Configuration saved");
        setConfigDirty(false);
        fetchSchedulerState();
      } else {
        toast.error("Failed to save configuration");
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(searchSymbol);
  };

  const formatAlpha = (alpha) => {
    if (alpha === null || alpha === undefined) return "-";
    const pct = (alpha * 100).toFixed(2);
    return `${pct}%`;
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return "0";
    return val.toLocaleString("en-US");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const progress = stats?.state?.progress;

  return (
    <div className="space-y-6" data-testid="alpha-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Stock Alpha</h1>
          <p className="text-muted-foreground mt-1">
            Cross-sectional daily alpha signals (stock return minus sector/cap bucket return)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={fetchDiagnostics}
            className="rounded-sm"
            title="Show diagnostics"
          >
            <Bug className="w-4 h-4" />
          </Button>
          {running ? (
            <>
              <Button variant="destructive" onClick={cancelRun} className="rounded-sm">
                <XCircle className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={forceReset}
                className="rounded-sm border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Force Reset
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => runPipeline("incremental")} className="rounded-sm">
                <Play className="w-4 h-4 mr-2" />
                Incremental
              </Button>
              <Button onClick={() => runPipeline("test")} className="rounded-sm">
                <TestTube className="w-4 h-4 mr-2" />
                Test Run
              </Button>
              <Button
                variant="outline"
                onClick={forceReset}
                className="rounded-sm border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                title="Force reset stuck pipelines"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Diagnostics Modal */}
      {showDiagnostics && diagnostics && (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bug className="w-4 h-4 text-yellow-400" />
                Alpha Pipeline Diagnostics
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowDiagnostics(false)}>
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
              <div className="p-3 bg-background rounded-sm">
                <p className="font-medium text-muted-foreground mb-2">Raw Data</p>
                <p>Status: <Badge variant={diagnostics.checks?.raw_data?.status === "ok" ? "default" : "destructive"}>{diagnostics.checks?.raw_data?.status}</Badge></p>
                <p>Rows: {diagnostics.checks?.raw_data?.total_rows?.toLocaleString()}</p>
                <p>Symbols: {diagnostics.checks?.raw_data?.symbols}</p>
                <p>Dates: {diagnostics.checks?.raw_data?.dates}</p>
              </div>
              <div className="p-3 bg-background rounded-sm">
                <p className="font-medium text-muted-foreground mb-2">Data Quality</p>
                <p>Sector Coverage: {diagnostics.checks?.raw_data_quality?.sector_coverage_pct}%</p>
                <p>Cap Bucket Coverage: {diagnostics.checks?.raw_data_quality?.cap_bucket_coverage_pct}%</p>
              </div>
              <div className="p-3 bg-background rounded-sm">
                <p className="font-medium text-muted-foreground mb-2">Exchange Mapping</p>
                <p>Profiles Found: {diagnostics.checks?.exchange_mapping?.profiles_found}/{diagnostics.checks?.exchange_mapping?.symbols_checked}</p>
                {diagnostics.checks?.exchange_mapping?.exchange_distribution && (
                  <div className="mt-1">
                    {Object.entries(diagnostics.checks.exchange_mapping.exchange_distribution).map(([ex, count]) => (
                      <span key={ex} className="mr-2">{ex}: {count}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-3 bg-background rounded-sm">
                <p className="font-medium text-muted-foreground mb-2">Alpha Output</p>
                <p>Status: <Badge variant={diagnostics.checks?.alpha_output?.status === "ok" ? "default" : "destructive"}>{diagnostics.checks?.alpha_output?.status}</Badge></p>
                <p>Rows: {diagnostics.checks?.alpha_output?.total_rows?.toLocaleString()}</p>
                <p>Symbols: {diagnostics.checks?.alpha_output?.symbols}</p>
                <p>Dates: {diagnostics.checks?.alpha_output?.dates}</p>
              </div>
            </div>
            {diagnostics.recent_runs && diagnostics.recent_runs.length > 0 && (
              <div className="mt-4">
                <p className="font-medium text-muted-foreground mb-2 text-xs">Recent Runs</p>
                <div className="space-y-1 text-xs">
                  {diagnostics.recent_runs.slice(0, 3).map((run, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-background rounded-sm">
                      <Badge variant={run.status === "completed" ? "default" : run.status === "failed" ? "destructive" : "outline"} className="text-xs">
                        {run.status}
                      </Badge>
                      <span className="font-mono">{run.run_id}</span>
                      <span>{run.run_type}</span>
                      <span className="text-muted-foreground">{run.dates_processed} dates</span>
                      <span className="text-muted-foreground">{run.rows_written} rows</span>
                      {run.errors?.length > 0 && (
                        <span className="text-red-400">{run.errors[0]}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Progress Bar */}
      {running && progress && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium capitalize">
                {progress.phase?.replace(/_/g, " ")}
              </span>
              <span className="text-sm text-muted-foreground">{progress.percent_complete}%</span>
            </div>
            <div className="w-full bg-background rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress.percent_complete}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{progress.processed}/{progress.total} dates</span>
              <span>{progress.current_item}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Alpha Records</p>
                <p className="text-2xl font-bold text-primary">
                  {formatNumber(stats?.total_alpha_rows)}
                </p>
              </div>
              <Activity className="w-8 h-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Symbols</p>
                <p className="text-2xl font-bold text-green-400">
                  {formatNumber(stats?.symbols_with_alpha)}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-green-400/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Dates Covered</p>
                <p className="text-2xl font-bold">
                  {formatNumber(stats?.dates_with_alpha)}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Coverage</p>
                <p className="text-2xl font-bold text-amber-400">
                  {stats?.coverage_percent || 0}%
                </p>
              </div>
              <Database className="w-8 h-8 text-amber-400/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Avg Alpha</p>
                <p className="text-2xl font-bold font-mono">
                  {formatAlpha(stats?.alpha_stats?.avg_alpha)}
                </p>
              </div>
              <Zap className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scheduler Status */}
      <Card className={`border-border rounded-sm ${schedulerState?.job_active ? 'border-green-500/30' : 'border-yellow-500/30'}`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Timer className={`w-5 h-5 ${schedulerState?.job_active ? 'text-green-400' : 'text-yellow-400'}`} />
              <div>
                <p className="font-medium">Daily Auto-Refresh</p>
                <p className="text-xs text-muted-foreground">
                  {schedulerState?.enabled 
                    ? `Scheduled at ${String(schedulerState?.hour_utc || 1).padStart(2, '0')}:${String(schedulerState?.minute || 0).padStart(2, '0')} UTC`
                    : "Disabled"
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {schedulerState?.next_run_at && (
                <span className="text-sm text-muted-foreground">
                  Next: {formatDate(schedulerState.next_run_at)}
                </span>
              )}
              <Badge variant={schedulerState?.job_active ? "default" : "secondary"}>
                {schedulerState?.job_active ? "Active" : schedulerState?.enabled ? "Pending" : "Disabled"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="overview" className="rounded-sm">Overview</TabsTrigger>
          <TabsTrigger value="browse" className="rounded-sm">Browse Data</TabsTrigger>
          <TabsTrigger value="runs" className="rounded-sm">Run Controls</TabsTrigger>
          <TabsTrigger value="history" className="rounded-sm">History</TabsTrigger>
          <TabsTrigger value="config" className="rounded-sm">Configuration</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Timeframe Selector */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-muted-foreground">Timeframe:</span>
                <div className="flex gap-1">
                  {[
                    { value: "1d", label: "1D" },
                    { value: "7d", label: "7D" },
                    { value: "1m", label: "1M" },
                    { value: "3m", label: "3M" },
                    { value: "6m", label: "6M" }
                  ].map((tf) => (
                    <Button
                      key={tf.value}
                      variant={alphaPeriod === tf.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAlphaPeriod(tf.value)}
                      className="rounded-sm px-3 h-8"
                    >
                      {tf.label}
                    </Button>
                  ))}
                </div>
                {alphaPeriod === "1d" && availableDates.length > 0 && (
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="bg-background border border-border rounded-sm px-2 py-1 text-sm ml-2"
                  >
                    {availableDates.map(d => (
                      <option key={d.date} value={d.date}>{d.date}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Top Positive Alpha */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    Top Positive Alpha
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {alphaPeriod === "1d" ? selectedDate : `Last ${alphaPeriod.toUpperCase()}`}
                  </Badge>
                </div>
                <CardDescription>
                  {alphaPeriod === "1d" 
                    ? "Stocks outperforming their sector/cap bucket today"
                    : `Avg alpha over ${alphaPeriod === "7d" ? "7 days" : alphaPeriod === "1m" ? "30 days" : alphaPeriod === "3m" ? "90 days" : "180 days"}`
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {topAlpha.positive.map((row, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-sm hover:bg-muted/50">
                        <div>
                          <span className="font-mono font-medium">{row.symbol}</span>
                          <span className="text-xs text-muted-foreground ml-2">{row.sector}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-green-400 font-mono font-medium">
                            +{formatAlpha(alphaPeriod === "1d" ? row.alpha : row.avg_alpha)}
                          </span>
                          {alphaPeriod !== "1d" && row.count && (
                            <span className="text-xs text-muted-foreground ml-2">
                              ({row.count}d)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {topAlpha.positive.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No alpha data available. Run the pipeline to compute alpha.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Top Negative Alpha */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-red-400" />
                    Top Negative Alpha
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {alphaPeriod === "1d" ? selectedDate : `Last ${alphaPeriod.toUpperCase()}`}
                  </Badge>
                </div>
                <CardDescription>
                  {alphaPeriod === "1d" 
                    ? "Stocks underperforming their sector/cap bucket today"
                    : `Avg alpha over ${alphaPeriod === "7d" ? "7 days" : alphaPeriod === "1m" ? "30 days" : alphaPeriod === "3m" ? "90 days" : "180 days"}`
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {topAlpha.negative.map((row, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-sm hover:bg-muted/50">
                        <div>
                          <span className="font-mono font-medium">{row.symbol}</span>
                          <span className="text-xs text-muted-foreground ml-2">{row.sector}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-red-400 font-mono font-medium">
                            {formatAlpha(alphaPeriod === "1d" ? row.alpha : row.avg_alpha)}
                          </span>
                          {alphaPeriod !== "1d" && row.count && (
                            <span className="text-xs text-muted-foreground ml-2">
                              ({row.count}d)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {topAlpha.negative.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No alpha data available. Run the pipeline to compute alpha.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Date Range Info */}
            <Card className="bg-card border-border rounded-sm lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm">Data Coverage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Oldest Date</p>
                    <p className="font-mono">{stats?.date_range?.oldest || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Newest Date</p>
                    <p className="font-mono">{stats?.date_range?.newest || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Raw Data Dates</p>
                    <p className="font-mono">{formatNumber(stats?.raw_dates_count)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Alpha Computed</p>
                    <p className="font-mono">{formatNumber(stats?.dates_with_alpha)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Browse Data Tab */}
        <TabsContent value="browse" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Alpha Data</CardTitle>
                  <CardDescription>Search and browse alpha records</CardDescription>
                </div>
                <form onSubmit={handleSearch} className="flex items-center gap-2">
                  <Input
                    placeholder="Search symbol..."
                    value={searchSymbol}
                    onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                    className="w-32 rounded-sm"
                  />
                  <Button type="submit" variant="outline" size="icon" className="rounded-sm">
                    <Search className="w-4 h-4" />
                  </Button>
                  {searchSymbol && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setSearchSymbol(""); setData([]); }}
                      className="rounded-sm"
                    >
                      Clear
                    </Button>
                  )}
                </form>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 font-medium">Symbol</th>
                      <th className="text-left py-2 px-3 font-medium">Date</th>
                      <th className="text-right py-2 px-3 font-medium">Alpha</th>
                      <th className="text-right py-2 px-3 font-medium">Daily Ret</th>
                      <th className="text-right py-2 px-3 font-medium">Bucket Ret</th>
                      <th className="text-left py-2 px-3 font-medium">Sector</th>
                      <th className="text-left py-2 px-3 font-medium">Cap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 px-3 font-mono font-medium">{row.symbol}</td>
                        <td className="py-2 px-3">{row.date}</td>
                        <td className={`py-2 px-3 text-right font-mono ${row.alpha >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {row.alpha >= 0 ? '+' : ''}{formatAlpha(row.alpha)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-muted-foreground">
                          {formatAlpha(row.daily_return)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-muted-foreground">
                          {formatAlpha(row.bucket_return)}
                        </td>
                        <td className="py-2 px-3 text-xs">{row.sector || "-"}</td>
                        <td className="py-2 px-3 text-xs capitalize">{row.cap_bucket?.replace("_", " ") || "-"}</td>
                      </tr>
                    ))}
                    {data.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-muted-foreground">
                          {searchSymbol ? "No data found for this symbol." : "Search for a symbol or run the pipeline to compute alpha."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Run Controls Tab */}
        <TabsContent value="runs" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Incremental */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Play className="w-4 h-4 text-green-400" />
                  Incremental
                </CardTitle>
                <CardDescription>
                  Compute alpha for missing dates only
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => runPipeline("incremental")}
                  disabled={running}
                  className="w-full rounded-sm"
                >
                  {running ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Run Incremental
                </Button>
              </CardContent>
            </Card>

            {/* Test Mode */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-yellow-400" />
                  Test Mode
                </CardTitle>
                <CardDescription>
                  Run with limited data for testing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => runPipeline("test")}
                  disabled={running}
                  className="w-full rounded-sm"
                  variant="secondary"
                >
                  {running ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                  Run Test
                </Button>
              </CardContent>
            </Card>

            {/* Full Recompute */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-orange-400" />
                  Full Recompute
                </CardTitle>
                <CardDescription>
                  Recompute alpha for all dates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => {
                    if (window.confirm("This will recompute ALL alpha values. This may take a long time. Continue?")) {
                      runPipeline("full-recompute");
                    }
                  }}
                  disabled={running}
                  className="w-full rounded-sm"
                  variant="outline"
                >
                  {running ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Full Recompute
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Warning: This may take several hours
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-sm">Run History</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2">Run ID</th>
                      <th className="text-left py-2">Type</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Started</th>
                      <th className="text-left py-2">Duration</th>
                      <th className="text-left py-2">Dates</th>
                      <th className="text-left py-2">Rows</th>
                      <th className="text-left py-2">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.recent_runs || []).map((run, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 font-mono text-xs">{run.run_id}</td>
                        <td className="py-2 text-xs">{run.run_type}</td>
                        <td className="py-2">
                          <Badge 
                            variant={run.status === "completed" ? "default" : 
                                     run.status === "running" ? "outline" : "destructive"}
                            className="text-xs"
                          >
                            {run.status}
                          </Badge>
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {formatDate(run.started_at)}
                        </td>
                        <td className="py-2 text-xs">{formatDuration(run.duration_seconds)}</td>
                        <td className="py-2 text-xs">{run.dates_processed || 0}</td>
                        <td className="py-2 text-xs">
                          <span className="text-blue-400">{formatNumber(run.rows_written || 0)}</span>
                        </td>
                        <td className="py-2 text-xs max-w-[200px]">
                          {run.status === "failed" && (run.errors?.length > 0 || run.error_message) && (
                            <span className="text-red-400 truncate block" title={run.errors?.[0] || run.error_message}>
                              {(run.errors?.[0] || run.error_message || "Unknown error").slice(0, 50)}
                              {(run.errors?.[0] || run.error_message || "").length > 50 && "..."}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Config Tab */}
        <TabsContent value="config" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Pipeline Configuration
                  </CardTitle>
                  <CardDescription>Configure alpha computation behavior</CardDescription>
                </div>
                {configDirty && (
                  <Button onClick={saveConfig} className="rounded-sm">
                    Save Changes
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Daily Scheduler */}
                <div className="space-y-4 md:col-span-2 lg:col-span-3 p-4 border border-border rounded-sm">
                  <h3 className="font-medium">Daily Auto-Refresh</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center justify-between">
                      <Label>Enable Daily Refresh</Label>
                      <Switch
                        checked={config.daily_refresh_enabled ?? true}
                        onCheckedChange={(checked) => {
                          setConfig({ ...config, daily_refresh_enabled: checked });
                          setConfigDirty(true);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hour (UTC)</Label>
                      <Input
                        type="number"
                        value={config.daily_refresh_hour_utc ?? 1}
                        onChange={(e) => {
                          setConfig({ ...config, daily_refresh_hour_utc: parseInt(e.target.value) || 0 });
                          setConfigDirty(true);
                        }}
                        className="bg-background rounded-sm"
                        min={0}
                        max={23}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Minute</Label>
                      <Input
                        type="number"
                        value={config.daily_refresh_minute ?? 0}
                        onChange={(e) => {
                          setConfig({ ...config, daily_refresh_minute: parseInt(e.target.value) || 0 });
                          setConfigDirty(true);
                        }}
                        className="bg-background rounded-sm"
                        min={0}
                        max={59}
                      />
                    </div>
                  </div>
                </div>

                {/* Processing Settings */}
                <div className="space-y-2">
                  <Label>Max Missing Dates per Run</Label>
                  <Input
                    type="number"
                    value={config.max_missing_dates_to_process ?? 90}
                    onChange={(e) => {
                      setConfig({ ...config, max_missing_dates_to_process: parseInt(e.target.value) || 90 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={1}
                    max={365}
                  />
                  <p className="text-xs text-muted-foreground">Cap incremental run size</p>
                </div>

                <div className="space-y-2">
                  <Label>DB Write Batch Size</Label>
                  <Input
                    type="number"
                    value={config.db_write_batch_size ?? 500}
                    onChange={(e) => {
                      setConfig({ ...config, db_write_batch_size: parseInt(e.target.value) || 500 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={100}
                    max={1000}
                  />
                  <p className="text-xs text-muted-foreground">Documents per bulk write</p>
                </div>

                <div className="space-y-2">
                  <Label>Max Gap Days (Price Fill)</Label>
                  <Input
                    type="number"
                    value={config.max_gap_days ?? 3}
                    onChange={(e) => {
                      setConfig({ ...config, max_gap_days: parseInt(e.target.value) || 3 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={1}
                    max={10}
                  />
                  <p className="text-xs text-muted-foreground">Max consecutive days to forward-fill</p>
                </div>

                <div className="space-y-2">
                  <Label>Test Symbols Limit</Label>
                  <Input
                    type="number"
                    value={config.test_symbols_limit ?? 100}
                    onChange={(e) => {
                      setConfig({ ...config, test_symbols_limit: parseInt(e.target.value) || 100 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={10}
                    max={500}
                  />
                  <p className="text-xs text-muted-foreground">Symbols for test mode</p>
                </div>

                <div className="space-y-2">
                  <Label>Test Last N Days</Label>
                  <Input
                    type="number"
                    value={config.test_last_n_days ?? 30}
                    onChange={(e) => {
                      setConfig({ ...config, test_last_n_days: parseInt(e.target.value) || 30 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={1}
                    max={90}
                  />
                  <p className="text-xs text-muted-foreground">Days for test mode</p>
                </div>
              </div>

              {/* Boolean toggles */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                  <div>
                    <Label>Enable Price Fill</Label>
                    <p className="text-xs text-muted-foreground">Forward-fill short price gaps</p>
                  </div>
                  <Switch
                    checked={config.enable_price_fill ?? true}
                    onCheckedChange={(checked) => {
                      setConfig({ ...config, enable_price_fill: checked });
                      setConfigDirty(true);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                  <div>
                    <Label>Drop Unknown Exchange</Label>
                    <p className="text-xs text-muted-foreground">Exclude stocks with no exchange info</p>
                  </div>
                  <Switch
                    checked={config.drop_unknown_exchange ?? true}
                    onCheckedChange={(checked) => {
                      setConfig({ ...config, drop_unknown_exchange: checked });
                      setConfigDirty(true);
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AlphaPage;
